import { JObject } from "../../../../../common/data/models";
import { buildFilterMetaToObject, buildSortParamsToString, PaginatedRequestParams, PaginatedResponse, Result } from "../../../../../common/pagination";
import { endpoints } from "../../../../../constants/endpoints";
import { tawreedHttpService } from "../../../../../http";
import { IBonusType } from "../../../../constants";
import { PartnerMapper, PartnerType } from "../../../../partners/data";
import { PartnersService } from "../../../../partners/domain";
import { CheckDeliverDTO, OrderDto, OrderItemDto } from "../../domain";
import { ConfirmAllDto } from "../../domain/dtos/actions/confirm-all";
import { OrderDeliveryPartners } from "../../domain/dtos/actions/orders-delivery-partners";
import { DeliveryPartnerDto } from "../../domain/dtos/delivery-partner";
import { OrderFilesDTO } from "../../domain/dtos/order-documents";
import { ConfirmReadyDto, ReadyToDeliveryDto } from "../../domain/dtos/ready-to-delivery";
import { HistoryItemMapper, OrderItemMapper, OrderMapper } from "../mappers";
import { DeliveryMapper } from "../mappers/delivery";
import { ReadyToDeliveryMapper } from "../mappers/ready-to-delivery";
import { BulkResponse, HistoryTrackingItem, Order, OrderItem, ReportUrl } from "../models";

export class OrderRepository {

    /**
     *
     */
    private partners = new PartnersService();

    /**
     * Search
     * @param arg
     * @param returned
     * @returns
     */
    public search(arg: PaginatedRequestParams, returned?: boolean): Promise<PaginatedResponse<OrderDto>> {
        const endpoint: string = returned ? endpoints.RETURNED_ORDERS_SEARCH : endpoints.ORDERS_SEARCH;
        const params = {
            sort: arg.sort ? buildSortParamsToString(arg.sort) : undefined,
            page: arg.page,
            size: arg.size
        };
        const data = { ...buildFilterMetaToObject(arg.keyword, arg.filters) };

        return tawreedHttpService
            .post<JObject, Result<JObject>>(endpoint, data, params, undefined, 'error')
            .then(res => {
                return {
                    ...res,
                    data: {
                        ...res.data!,
                        content: res.data!.content.map(e => this.fromJsonToOrderDto2(e)) ?? [],
                    }
                }
            });
    }

    public async getDetails(id: number, returned?: boolean): Promise<OrderDto> {
        const endpoint: string = returned ? endpoints.RETURNED_ORDERS_GET : endpoints.ORDER_GET;
        return tawreedHttpService
            .post<number, Order>(endpoint, id, undefined, undefined, 'error')
            .then(res => this.fromJsonToOrderDto1(res.data));
    }
    public async getPublicDetails(id: number, returned?: boolean): Promise<OrderDto> {
        const endpoint: string = returned ? endpoints.RETURNED_ORDERS_GET : endpoints.ORDER_GET;
        return tawreedHttpService
            .post<number, Order>(endpoint, id, undefined, undefined, 'error')
            .then(res => this.fromJsonToOrderDto3(res.data));
    }

    public async cancel(id: number, reasonCode: number, reasonAr:string | undefined,
        reasonEn: string | undefined): Promise<OrderDto> {
        const form = {
            orderId: id,
            cancelReasonId: reasonCode,
            reasonAr: reasonAr,
            reasonEn: reasonEn

        };
        return tawreedHttpService
            .post<JObject, JObject>(endpoints.ORDER_CANCEL, form)
            .then(res => this.fromJsonToOrderDto1(res.data));

    }

    public async cancelReturn(id: number): Promise<OrderDto> {
        const form = {
            orderId: id,
        };
        return tawreedHttpService
            .post<JObject, JObject>(endpoints.ORDER_RETURN_CANCEL, form)
            .then(res => this.fromJsonToOrderDto1(res.data));

    }

    public async ship(id: number): Promise<OrderDto> {
        return tawreedHttpService
            .post<JObject, JObject>(endpoints.ORDER_SHIP, { "orderId": id })
            .then(res => this.fromJsonToOrderDto1(res.data));
    }

    public async confirmAndReady(readyToDeliveryDto: ConfirmReadyDto): Promise<OrderDto> {

        return tawreedHttpService
            .post<JObject, JObject>(endpoints.ORDER_CONFIRM_AND_READY,
                ReadyToDeliveryMapper.toConfirmReadyJson(readyToDeliveryDto))
            .then(res => this.fromJsonToOrderDto1(res.data));
    }

    public async confirmReadyAndShip(readyToDeliveryDto: ConfirmReadyDto): Promise<OrderDto> {

        return tawreedHttpService
            .post<JObject, JObject>(endpoints.ORDER_CONFIRM_READY_SHIP,
                ReadyToDeliveryMapper.toConfirmReadyJson(readyToDeliveryDto))
            .then(res => this.fromJsonToOrderDto1(res.data));
    }

    public async readyAndShip(readyToDeliveryDto: ConfirmReadyDto): Promise<OrderDto> {

        return tawreedHttpService
            .post<JObject, JObject>(endpoints.ORDER_READY_SHIP,
                ReadyToDeliveryMapper.toConfirmReadyJson(readyToDeliveryDto))
            .then(res => this.fromJsonToOrderDto1(res.data));
    }

    public async ready(readyToDeliveryDto: ReadyToDeliveryDto): Promise<OrderDto> {

        return tawreedHttpService
            .post<JObject, JObject>(endpoints.ORDER_READY,
                ReadyToDeliveryMapper.toJson(readyToDeliveryDto))
            .then(res => this.fromJsonToOrderDto1(res.data));
    }

    public async confirm(orderId: number, carrierId: number): Promise<OrderDto> {
        const form = {
            orderId: orderId,
            carrierId: carrierId,
        };
        return tawreedHttpService
            .post<JObject, JObject>(endpoints.ORDER_CONFIRM, form)
            .then(res => this.fromJsonToOrderDto1(res.data));
    }
    public async confirmAll(confirmAllDto:ConfirmAllDto[]): Promise<BulkResponse[]> {
        const form = confirmAllDto.map((dto) =>
            {
                return {
                    orderId: dto.orderId,
                    carrierId: dto.carrierId,
                };
        });
        return tawreedHttpService
            .post<JObject, JObject>(endpoints.ORDER_BULK_ACTION.replace(':action','confirm'), form)
            .then(res => this.fromJsonToBulkResponse(res.data));
    }
    public async review(orderId: number): Promise<OrderDto> {
        const form = {
            orderId: orderId,
        };
        return tawreedHttpService
            .post<JObject, JObject>(endpoints.ORDER_REVIEW, form)
            .then(res => this.fromJsonToOrderDto1(res.data));
    }
    public async reviewAll(orderIds: number[]): Promise<BulkResponse[]> {
        const form = orderIds.map(id =>
            {
                return {
                    orderId: id,
                };
        });
        return tawreedHttpService
            .post<JObject, JObject>(endpoints.ORDER_BULK_ACTION.replace(':action','review'), form)
            .then(res => this.fromJsonToBulkResponse(res.data));
    }

    public async applyDiscount(orderId: number, type: string, value: number): Promise<OrderDto> {
        const form = {
            orderId: orderId,
            invoiceDiscountType: type,
            invoiceDiscountValue: value
        };
        return tawreedHttpService
            .post<JObject, JObject>(endpoints.ORDER_APPLY_DISCOUNT, form)
            .then(res => this.fromJsonToOrderDto1(res.data));
    }
    public async changeCarrier(orderId: number, carrierId: number): Promise<OrderDto> {
        const form = {
            orderId: orderId,
            carrierId: carrierId,
        };
        return tawreedHttpService
            .post<JObject, JObject>(endpoints.ORDER_CARRIER_CHANGE, form)
            .then(res => this.fromJsonToOrderDto1(res.data));
    }

    public async deliver(data: any): Promise<OrderDto> {
        return tawreedHttpService
            .post<JObject, JObject>(endpoints.ORDER_DELIVER, data)
            .then(res => this.fromJsonToOrderDto1(res.data));
    }
    public async confirmReadyShipAndDeliver(data: any): Promise<OrderDto>{
        return tawreedHttpService
            .post<JObject, JObject>(endpoints.ORDER_CONFIRM_READY_SHIP_DELIVER,
                ReadyToDeliveryMapper.toConfirmReadyJson(data))
            .then(res => this.fromJsonToOrderDto1(res.data));
    }
    public async readyShipAndDeliver(data:any):Promise<OrderDto>{
        return tawreedHttpService
        .post<JObject, JObject>(endpoints.ORDER_READY_SHIP_DELIVER,
            ReadyToDeliveryMapper.toConfirmReadyJson(data))
        .then(res => this.fromJsonToOrderDto1(res.data));
    }

    public async shipAndDeliver(data:any):Promise<OrderDto>{
        return tawreedHttpService
        .post<JObject, JObject>(endpoints.ORDER_SHIP_DELIVER,
            ReadyToDeliveryMapper.toConfirmReadyJson(data))
        .then(res => this.fromJsonToOrderDto1(res.data));
    }
    public async addNote(data: any): Promise<OrderDto>{
        return tawreedHttpService
            .post<JObject, JObject>(endpoints.ORDER_ADD_NOTE, data)
            .then(res => this.fromJsonToOrderDto1(res.data));
    }
    public async checkDeliver(orderId: number): Promise<CheckDeliverDTO>{
        return tawreedHttpService
        .post<number, JObject>(endpoints.ORDER_DELIVER_CHECK, orderId, undefined, undefined, 'error')
        .then(res =>
            {
                if(res && res.data){
            return {
                requireOtp: res.data.requireOtp,
                otpLength: res.data.otpLength
            }
        }
            else{
                return {
                    requireOtp: false,
                    otpLength: 4
                }
            }
    }
);
    }
    public async return(returned: boolean | undefined, id: number, reasonCode: number, reasonAr:string | undefined,
        reasonEn: string | undefined): Promise<OrderDto> {
        return tawreedHttpService
            .post<JObject, JObject>(endpoints.ORDER_RETURN, {orderId:id,reutrnReasonCode: reasonCode,
                reasonAr: reasonAr, reasonEn: reasonEn})
            .then(res => this.fromJsonToOrderDto1(res.data));
    }

    public async getDeliveryPartners(id: number): Promise<DeliveryPartnerDto[]> {
        return tawreedHttpService
            .post<number, JObject>(endpoints.ORDER_GET_DELIVERY_PARTNER, id, undefined, undefined, 'error')
            .then(res => {
                let result: DeliveryPartnerDto[] = [];
                res.data!.forEach((element: JObject) => {
                    result.push(DeliveryMapper.toDto(element));
                });
                return result;
            }

            );
    }
    public async getDeliveryPartnersForOrders(ids: number[]): Promise<OrderDeliveryPartners[]> {
        return tawreedHttpService
            .post<number[], JObject>(endpoints.ORDERS_GET_DELIVERY_PARTNER, ids, undefined, undefined, 'error')
            .then(res => {
                let result: OrderDeliveryPartners[] = [];
                res.data!.forEach((orderElement: JObject) => {
                    var deliveries: DeliveryPartnerDto[] =[];
                    orderElement.deliveryPartnerDtos.forEach((orderElement: JObject) => {

                        deliveries.push(DeliveryMapper.toDto(orderElement));
                    });
                    result.push({
                        orderId: orderElement.orderId,
                        partners: deliveries
                    })
                });
                return result;
            }

            );
    }
    public async updateItem(item: OrderItemDto): Promise<OrderDto> {
        const form = {
            orderItemId: item.orderItemId,
            orderId: item.orderId,
            quantity: item.quantity,
            retailPrice: item.retailPrice,
            bonus: item.bonus,
            discountValue: item.discountValue,
            bonusType: item.bonusType,
            appliedBonus: item.appliedBonus,
            bonuses: item.bonuses?.map(el => ({ ...el, bonus: el.bonusType === IBonusType.EXTRA_ITEMS ? item.bonus : item.discountBonus })) ?? []
        };
        return tawreedHttpService
            .post<JObject, JObject>(endpoints.ORDER_ITEM_UPDATE, form)
            .then(res => this.fromJsonToOrderDto1(res.data));
    }

    private async fromJsonToOrderDto1(json: JObject | undefined): Promise<OrderDto> {
        if (!json) {
            return Promise.reject();
        }
        return this.partners.getDetails(json.customerId).then(customer => {
            return this.partners.getDetails(json.storeId).then(store => {
                if(json.carrierId){
                    return this.partners.getDetails(json.carrierId).then(carrier => {
                        return OrderMapper.toDto(OrderMapper.fromJson2(json, customer, store,carrier));
                        });
                }
                else{
                    return OrderMapper.toDto(OrderMapper.fromJson(json, customer, store));
                }
               
            });
        });
    }
    private async fromJsonToOrderDto3(json: JObject | undefined): Promise<OrderDto> {
        if (!json) {
            return Promise.reject();
        }
        return OrderMapper.toDto(OrderMapper.fromJson(json,
             {"partnerId": json?.customerId,"partnerName":json?.customer}, 
             {"partnerId": json?.storeId,"partnerName":json?.store}));
        
    }

    private fromJsonToOrderDto2(json: JObject | undefined): OrderDto {

        const customer = PartnerMapper.toDto(PartnerMapper.fromJson({
            partnerId: json?.customerId,
            partnerName: json?.customer,
            partnerType: PartnerType.Customer,
            state:json?.customerStateName,
            contact: {
                contactId: json?.contact?.contactId,
                contactName: json?.contact?.contactName,
                email: json?.contact?.email,
                mobileNo: json?.contact?.mobileNo,
            },
            address: {
                addressId: json?.address?.addressId,
                state: json?.address?.state,
                stateId: json?.address?.stateId,
                zoneId: json?.address?.zoneId,
                latitude: json?.address?.latitude,
                longitude: json?.address?.longitude,
                street: json?.address?.street,
                fullAddress: json?.address?.fullAddress,
            },
        }));

        const store = PartnerMapper.toDto(PartnerMapper.fromJson({
            partnerId: json?.storeId,
            partnerName: json?.store,
            partnerType: PartnerType.Store,
            contact: {
                contactId: json?.storeContact?.contactId,
                contactName: json?.storeContact?.contactName,
                email: json?.storeContact?.email,
                mobileNo: json?.storeContact?.mobileNo,
            },
            address: {
                addressId: json?.storeAddress?.addressId,
                state: json?.storeAddress?.state,
                stateId: json?.storeAddress?.stateId,
                zoneId: json?.storeAddress?.zoneId,
                latitude: json?.storeAddress?.latitude,
                longitude: json?.storeAddress?.longitude,
                street: json?.storeAddress?.street,
                fullAddress: json?.storeAddress?.fullAddress,
            },
        }));
        return OrderMapper.toDto(OrderMapper.fromJson(json!, customer, store));
    }
    public async uploadOrderFile(data: OrderFilesDTO): Promise<boolean> {
        return tawreedHttpService
            .post<JObject, JObject>(endpoints.ORDER_UPLOAD_DOCUMENT, data)
            .then((res) => {
                return true
            });
    }
    public searchItems(cartId: number, arg: PaginatedRequestParams): Promise<PaginatedResponse<OrderItemDto>> {
        const params = {
            sort: arg.sort ? buildSortParamsToString(arg.sort) : undefined,
            page: arg.page,
            size: arg.size
        };

        return tawreedHttpService
            .post<number, Result<OrderItem>>(endpoints.ORDER_GET_ITEMS, cartId, params, undefined, 'error')
            .then(res => {
                return {
                    ...res!,
                    data: {
                        ...res!.data!, content: res!.data!.content.map(e => {
                            return OrderItemMapper.toDto(e)
                        }) ?? [],
                    }
                }
            });
    }

    public getOrderHistory(arg: PaginatedRequestParams): Promise<PaginatedResponse<HistoryTrackingItem>> {
        const params = {
            sort: arg.sort ? buildSortParamsToString(arg.sort) : undefined,
            page: arg.page,
            size: arg.size
        };
        const data = { ...buildFilterMetaToObject(arg.keyword, arg.filters) };

        return tawreedHttpService
            .post<JObject, Result<JObject>>(endpoints.ORDER_GET_HISTORY, data, params, undefined, 'error')
            .then(res => {
                return {
                    ...res,
                    data: {
                        ...res.data!,
                        content: res.data!.content.map(e => HistoryItemMapper.fromJson(e)) ?? [],
                    }
                }
            });
    }
    public getSalesOrdersURL():Promise<ReportUrl>{
        return tawreedHttpService
        .post<JObject, JObject>(endpoints.ORDER_REPORT_URL, {}, undefined, undefined, 'error')
        .then(res => {
            return {
                docReportURI: res.data?.docReportURI,
                htmlReportURI: res.data?.htmlReportURI,
                xlsReportURI: res.data?.xlsReportURI,
                pdfReportURI: res.data?.pdfReportURI,

            }
        });
    }
    
    private async fromJsonToBulkResponse(json: JObject | undefined): Promise<BulkResponse[]> {
        if (!json) {
            return Promise.reject();
        }
        return json.map((item:JObject) =>{
            return {
                orderId: item.orderId,
                success: item.success,
                reason: item.reason,
            }
        })
    }
    public async receive(orderId: number, otp: string): Promise<boolean> {
        const form = {
            orderId: orderId,
            otp: otp,
        };
        return tawreedHttpService
            .post<JObject, JObject>(endpoints.ORDER_RECEIVE, form)
            .then(res => res.data ? true : false  );
    }

}


